import React__default from 'react';
import classNames from 'classnames';
import { determineDataTestIdPrefix, getBaseProps, getAriaProps } from '../../../helpers/propHelpers.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '../Badge/Badge.js';

class Tab extends React__default.Component {
    constructor(props) {
        super(props);
        this.dataTestId = determineDataTestIdPrefix(this.props, 'tab');
        this.handleInitialFocus = () => {
            if (this.props.tabFocused === '') {
                this.props.handleTabFocus(this.props.value);
                this.buttonInput.current.focus();
            }
        };
        this.getTabClasses = (classes) => {
            return classNames({
                Tab: true,
                'Tab--active': this.props.value === this.props.selectedValue,
            }, classes);
        };
        this.getTabIconClasses = () => {
            var _a;
            return classNames({
                Tab__icon: true,
                'Tab__icon--only': !((_a = this.props) === null || _a === void 0 ? void 0 : _a.label),
            });
        };
        this.getCounterClasses = (counterClasses) => {
            return classNames('Tab__counter', counterClasses);
        };
        this.handleButtonClick = (value) => {
            if (this.props.handleClick) {
                this.props.handleClick();
            }
            this.props.handleTabChange(value);
        };
        this.handleButtonKeyDown = (e) => {
            const key = e.key;
            const { childValues } = this.props;
            const lastIndex = childValues.length > 0 ? childValues.length - 1 : 0;
            const currentIndex = childValues.indexOf(this.props.value);
            const nextTabIndex = currentIndex < lastIndex ? currentIndex + 1 : 0;
            const previousTabIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
            if (e.key === 'Enter' && this.props.tabFocused !== '') {
                this.props.handleTabChange(this.props.tabFocused);
            }
            if (this.props.direction === 'vertical') {
                switch (key) {
                    case 'ArrowDown':
                        this.props.handleTabFocus(childValues[nextTabIndex]);
                        break;
                    case 'ArrowUp':
                        this.props.handleTabFocus(childValues[previousTabIndex]);
                        break;
                }
            }
            else {
                switch (key) {
                    case 'ArrowRight':
                        this.props.handleTabFocus(childValues[nextTabIndex]);
                        this.props.handleTabChange(childValues[nextTabIndex]);
                        break;
                    case 'ArrowLeft':
                        this.props.handleTabFocus(childValues[previousTabIndex]);
                        this.props.handleTabChange(childValues[previousTabIndex]);
                        break;
                }
            }
        };
        this.buttonInput = React__default.createRef();
    }
    componentDidUpdate(prevProps) {
        if (prevProps.tabFocused !== this.props.tabFocused && this.props.value === this.props.tabFocused) {
            this.buttonInput.current.focus();
        }
    }
    render() {
        return (React__default.createElement("button", { "data-testid": `${this.dataTestId}-${this.props.value}`, className: this.getTabClasses(this.props.className), onClick: () => this.handleButtonClick(this.props.value), onKeyDown: this.handleButtonKeyDown, onFocus: this.handleInitialFocus, ref: this.buttonInput, role: 'tab', tabIndex: this.props.value === this.props.selectedValue ? 0 : -1 },
            this.props.icon && !(this.props.size === 'condensed') && (React__default.createElement(FontAwesomeIcon, Object.assign({}, this.props.icon, { className: this.getTabIconClasses(), icon: this.props.icon.icon, "data-testid": `${this.dataTestId}-icon` }))),
            this.props.label,
            this.props.counter ? (React__default.createElement(Badge, { variant: 'pill', "data-testid": `${this.dataTestId}-counter-${this.props.value}`, className: this.getCounterClasses(this.props.counterClasses) }, this.props.counter)) : (React__default.createElement("div", null)),
            React__default.createElement("div", { className: 'after' })));
    }
}
const TabSeparator = () => {
    return React__default.createElement("div", { className: `TabSeparator` });
};
class Tabs extends React__default.Component {
    constructor(props) {
        super(props);
        this.handleTabChange = (value) => {
            this.props.handleChange(value);
            this.setState({ tabFocused: value });
        };
        this.handleTabFocus = (value) => {
            this.setState({ tabFocused: value });
        };
        this.getTabsClasses = () => {
            var _a;
            let activeVariant = 'default';
            if (((_a = this.props) === null || _a === void 0 ? void 0 : _a.direction) === 'vertical') {
                activeVariant = 'vertical';
            }
            else if (this.props.variant === 'secondary') {
                activeVariant = 'secondary';
            }
            else if (this.props.variant === 'vertical') {
                activeVariant = 'vertical';
            }
            return classNames({
                Tabs: true,
                'Tabs--v-default': activeVariant === 'default',
                'Tabs--v-secondary': activeVariant === 'secondary',
                'Tabs--v-vertical': activeVariant === 'vertical',
                'Tabs--vertical': activeVariant === 'vertical',
                'Tabs--horizontal': activeVariant !== 'vertical',
                'Tabs--condensed': this.props.size === 'condensed',
            });
        };
        this.baseProps = getBaseProps(this.props);
        this.ariaProps = getAriaProps(this.props);
        this.state = {
            tabFocused: '',
        };
        this.childValues = React__default.Children.map(this.props.children, (child) => {
            if (React__default.isValidElement(child)) {
                return child.props.value;
            }
        });
    }
    render() {
        var _a;
        const dataTestId = determineDataTestIdPrefix(this.props, 'tabs');
        const childrenWithProps = React__default.Children.map(this.props.children, (child, index) => {
            var _a;
            const { handleTabChange, handleTabFocus, childValues } = this;
            const { selectedValue, size } = this.props;
            const direction = this.props.variant === 'vertical' || ((_a = this.props) === null || _a === void 0 ? void 0 : _a.direction) === 'vertical' ? 'vertical' : 'horizontal';
            const tabFocused = this.state.tabFocused;
            const childProp = {
                selectedValue,
                size,
                direction,
                handleTabChange,
                tabFocused,
                handleTabFocus,
                childValues,
            };
            if (React__default.isValidElement(child)) {
                return React__default.cloneElement(child, childProp, handleTabChange);
            }
            return child;
        });
        return (React__default.createElement("div", Object.assign({}, this.baseProps, this.ariaProps, { role: 'tablist', "data-testid": `${dataTestId}`, className: classNames(this.getTabsClasses(), (_a = this.props) === null || _a === void 0 ? void 0 : _a.className) }), childrenWithProps));
    }
}
Tabs.TabItem = Tab;
Tabs.TabSeparator = TabSeparator;

export { Tabs, Tabs as default };
//# sourceMappingURL=Tabs.js.map
